import dayjs from 'dayjs';
import { Note, NoteType } from '../../utils/note'

// Task states for filtering (items starting with *)
export enum TaskState {
  OPEN = 'task-open',
  DONE = 'task-done',
  SCHEDULED = 'task-scheduled',
  CANCELLED = 'task-cancelled',
}

// Checklist states for filtering (items starting with +)
export enum ChecklistState {
  OPEN = 'checklist-open',
  DONE = 'checklist-done',
  SCHEDULED = 'checklist-scheduled',
  CANCELLED = 'checklist-cancelled',
}

// Level options for filtering
export enum Level {
  TASKS = 'tasks',
  NOTES = 'notes',
  EVENTS = 'events',
}

// Date range options
export type DateRange =
  | { type: 'thisMonth' }
  | { type: 'thisWeek' }
  | { type: 'thisQuarter' }
  | { type: 'thisYear' }
  | { type: 'rolling30Days' }
  | { type: 'future' }
  | { type: 'past' }
  | { type: 'custom'; start: Date; end: Date }

// Task item with metadata for grouping
export type TaskItem = {
  content: string
  state: string
  bullet: string
  scheduledDate?: string
  tag?: string
  lineIndex: number
  sourceNote: Note
  originalLine: string // Store the original line content for accurate task finding
}

// Grouped tasks by date
export type TaskGroup = {
  date: string // YYYY-MM-DD format or 'today'
  displayDate: string // Human readable date
  tasks: TaskItem[]  
  actualDate: dayjs.Dayjs
}

// Generic item that can be either a note or individual task
export type FilterItem = {
  type: 'note' | 'task'
  data: Note | TaskItem
  groupKey: string // For grouping (note path, date, etc.)
  displayKey: string // Human readable group name
}

// Grouped filter items
export type FilterGroup = {
  key: string
  displayName: string
  items: FilterItem[]
}

// Filter configuration with strongly typed properties
export type FilterConfig = {
  noteType?: NoteType[]
  taskState?: TaskState[]
  checklistState?: ChecklistState[]
  dateRange?: DateRange
  folder?: string[]
  searchTerm?: string
  level?: Level
}

// Type for the sort direction
export type SortDirection = 'asc' | 'desc'

// Type for the sort configuration
export type SortConfig = {
  direction: SortDirection
}

// Group by options
export enum GroupByOption {
  PATH = 'path', // Group by note path
  NOTE_PATH = 'notePath', // Legacy alias
  DATE = 'date', // Group by date
}

// Type for the group configuration
export type GroupConfig = {
  // Changed from boolean to enum for dropdown selection
  option: GroupByOption
}

// Type for the filter view configuration
export type FilterViewConfig = {
  filters: FilterConfig
  sort: SortConfig
  group: GroupConfig
}

// Type for the filtered notes result
export type FilteredNotesResult = {
  filteredNotes: Note[]
  taskGroups?: TaskGroup[] // For when level is tasks
  taskLimitReached: boolean
  totalTaskCount: number
  isLoading: boolean
  error: Error | undefined
}
